/**
 * What is coming, and how much of it is actually known.
 *
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE
 *
 * Five sources feed a cashflow forecast and they are not equally true. A total
 * that adds "$9,649.91 owed to the IRS on 15 October" to "we usually pay about
 * $500 on the 18th" is the invented due date `receivables.ts` warns about,
 * wearing a subtotal. So every item carries its CERTAINTY, and the caller is
 * given the bands separately as well as together -- never one reassuring number
 * whose parts cannot be told apart.
 *
 *   known      a payroll tax the books owe, dated by the API's deposit plan (src/payroll/deposits.ts)
 *   due        a bill or invoice whose terms somebody recorded (migration 0036)
 *   committed  a standing payment the operator recorded (migration 0037)
 *   projected  a payroll run that has not happened yet
 *
 * WHY THIS IS ASSEMBLED HERE, AND WHY THE PAYROLL TAXES ARE NOT
 *
 * Every input already has an endpoint, so composing them is the client's job.
 * The payroll taxes are not composed here. GET /payroll/deposits plans them
 * from the ledger -- every liability on 2410 to 2450, grouped into the payments
 * Publication 15 and Texas require, dated, with what the bank has already paid
 * laid against it -- and this file reads what is still outstanding. A preview
 * arrives with its deposit date from the same plan.
 *
 * It took two steps to get here, both in payroll stage 4 (2026-09-22). This
 * file used to call a browser copy of the date rule, `lib/deadlines.ts`; the
 * rule moved to the server instead of being copied. It then charged each run's
 * whole liability on its deposit date, which could not see a payment: a deposit
 * paid early stayed in the forecast and was subtracted a second time from a
 * bank balance it had already left, an overdue one fell out of the window and
 * made the forecast look cheaper, and a liability accrued by hand, as all of
 * 2026's first half was, never appeared at all.
 *
 * WHAT IS DELIBERATELY ABSENT
 *
 * MERCURY'S PENDING TRANSACTIONS, which would be the fourth band -- money
 * committed but not settled. `sync/classify.ts` skips them on purpose: §9.1
 * says the amount is not final, so nothing pending is ever written to the
 * database and there is nothing here to read. Surfacing them would need a store
 * the sync does not have and a live fetch on every load of this screen. Left
 * out, and said out loud, rather than approximated.
 *
 * RECURRING COMMITMENTS ARE HERE NOW, as of migration 0037, and they get their
 * own band rather than joining `due`. A bill with terms is a document somebody
 * signed; a commitment is the operator saying "this leaves on the 18th". Both
 * are more certain than a projection and less certain than a statutory deadline,
 * and collapsing them would hide which of the two a figure came from -- which
 * is the only question worth asking when one of them turns out to be wrong.
 *
 * Mercury's API still has no endpoint for scheduled or recurring payments
 * (verified against its catalogue, 2026-09-16), so a commitment can only come
 * from the operator recording one. `detectCommitmentPatterns` on the server
 * OFFERS candidates from the transactions; it never records one.
 */

import type {
  DepositObligation,
  InvoiceRow,
  OpenBill,
  PayFrequency,
  RecurringCommitment,
  RunPreview,
} from '../api.js';
import { formatCents } from './money.js';

/**
 * The four rhythms. `PayFrequency` and a commitment's cadence are the same four
 * strings, enforced identically by two migrations (0034 and 0037), so they are
 * one type here rather than two that have to be kept equal by hand.
 */
export type Cadence = PayFrequency;

export type Certainty = 'known' | 'due' | 'committed' | 'projected';

/** Most certain first. The screen renders bands in this order. */
export const CERTAINTIES: readonly Certainty[] = ['known', 'due', 'committed', 'projected'];

export interface CashflowItem {
  /** YYYY-MM-DD. */
  date: string;
  label: string;
  /** Why this is owed, in the operator's terms. */
  detail: string;
  /** Negative leaves the account; positive arrives. */
  amountCents: number;
  certainty: Certainty;
  /** What produced it, for the drill-down. */
  source: 'payroll' | 'bill' | 'invoice' | 'commitment' | 'projected-payroll';
  /** The deposit, entry or commitment behind it, so a row links to its evidence. */
  link?: { kind: 'deposit' | 'entry' | 'commitment'; id: string };
}

/**
 * A federal deposit that is owed and has no date, because no deposit schedule
 * is recorded for its pay date (migration 0048). It cannot be placed on a dated
 * list, and dropping it would make the forecast look cheaper than it is -- so it
 * is returned apart, and the screens say it exists.
 */
export interface UndatedDeposit {
  /** The pay it is for: one date, or the first and last of a month's. */
  firstPayDate: string;
  lastPayDate: string;
  amountCents: number;
  certainty: Extract<Certainty, 'known' | 'projected'>;
}

export interface CashflowResult {
  items: CashflowItem[];
  /** Owed, not dated, and not in `items` or in any total. */
  undated: UndatedDeposit[];
  /** Totals by certainty, never merged. */
  outflowCents: Record<Certainty, number>;
  inflowCents: Record<Certainty, number>;
  from: string;
  to: string;
}

const ZERO: Record<Certainty, number> = { known: 0, due: 0, committed: 0, projected: 0 };

/** The last day of the quarter `date` falls in. The default horizon. */
export function quarterEnd(date: string): string {
  const at = new Date(`${date}T00:00:00Z`);
  const quarter = Math.floor(at.getUTCMonth() / 3);
  // Day 0 of the month after the quarter's last month.
  return new Date(Date.UTC(at.getUTCFullYear(), quarter * 3 + 3, 0)).toISOString().slice(0, 10);
}

/** `days` after `date`, as a calendar date. */
export function daysAfter(date: string, days: number): string {
  const at = new Date(`${date}T00:00:00Z`);
  return new Date(at.getTime() + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The date after `date`, on `cadence`'s rhythm.
 *
 * ONE FUNCTION FOR PAYROLL AND FOR COMMITMENTS. It was `nextPayDate` until
 * commitments needed exactly the same arithmetic, and two copies of a month-end
 * clamp is two chances to fix one of them. The four cadences are deliberately
 * the same four `employee_pay_rates` and `recurring_commitments` both allow.
 *
 * THE MONTH-END CLAMP IS THE WHOLE REASON THIS IS NOT `daysAfter`. A monthly
 * run paid on 31 January has no 31 February to land on, and JavaScript's Date
 * rolls that forward to 2 or 3 March depending on the year -- silently, and a
 * projection two days into the wrong month is one nobody looks at twice. The
 * day is clamped to the target month's last day instead, which is what a
 * business paying on the last of the month actually does.
 */
export function nextOccurrence(date: string, cadence: Cadence): string {
  return occurrenceAt(date, cadence, 1);
}

/**
 * The `n`th occurrence after `anchor`, measured FROM THE ANCHOR EVERY TIME.
 *
 * THE CLAMP MUST NOT STICK, and the first version of `occurrencesBetween`
 * walked step by step from its previous result, so it did. A commitment on the
 * 31st went 31 August, 30 September, then **30 October** -- September's clamp
 * became the new day of the month and every later date inherited it. The drift
 * is one day, it is silent, and it is invisible until somebody compares the
 * forecast against the bank. A test caught it.
 *
 * Postgres behaves the same way for `starts_on + n * interval '1 month'`, which
 * is what `missedCommitments` uses, so anchoring here is also what keeps the
 * two implementations of one schedule agreeing.
 */
function occurrenceAt(anchor: string, cadence: Cadence, n: number): string {
  if (cadence === 'weekly') return daysAfter(anchor, 7 * n);
  if (cadence === 'biweekly') return daysAfter(anchor, 14 * n);

  const at = new Date(`${anchor}T00:00:00Z`);
  const months = (cadence === 'monthly' ? 1 : 3) * n;
  const year = at.getUTCFullYear();
  const month = at.getUTCMonth() + months;
  // Day 0 of the month after the target is the target's last day.
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(at.getUTCDate(), lastDay);
  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
}

/**
 * Every date a commitment falls on between `from` and `to`.
 *
 * COUNTED FROM `startsOn`, NOT WALKED FROM THE LAST RESULT. A monthly
 * commitment starting on 31 January lands on 28 February and then on 31 March;
 * walking from the clamped February date gives 28 March instead, and the lost
 * days accumulate. `occurrenceAt` measures every occurrence from the anchor.
 *
 * The walk is bounded. A weekly commitment started years ago and never ended
 * would otherwise iterate once per week since then on every render; the cap is
 * generous enough that no real schedule reaches it and small enough that a bad
 * date cannot hang the screen.
 */
export function occurrencesBetween(
  commitment: { startsOn: string; endsOn: string | null; cadence: Cadence },
  from: string,
  to: string,
): string[] {
  const dates: string[] = [];
  for (let step = 0; step < 2_000; step += 1) {
    const at = occurrenceAt(commitment.startsOn, commitment.cadence, step);
    if (at > to) break;
    if (commitment.endsOn !== null && at > commitment.endsOn) break;
    if (at >= from) dates.push(at);
  }
  return dates;
}

export interface ForecastInput {
  from: string;
  to: string;
  /**
   * What the books owe on the payroll tax accounts, as the API plans it (GET
   * /payroll/deposits): each payment Publication 15 or Texas requires, with its
   * due date and what the bank has not yet paid of it.
   */
  deposits: DepositObligation[];
  bills: OpenBill[];
  invoices: InvoiceRow[];
  /** Standing payments the operator recorded (migration 0037). */
  commitments: RecurringCommitment[];
  /**
   * The next pay run, priced by the server's own `previewPayrollRun` so the
   * figure cannot drift from what a real run would calculate. Null when there
   * is nothing to project -- no pay rate, no tax year, or the operator asked
   * not to.
   */
  projected: { payDate: string; preview: RunPreview } | null;
}

/** No schedule for the projected pay date, and a deposit owed: the one case with no date to put it on. */
function undatedDeposit(deadlines: RunPreview['deadlines']): boolean {
  return deadlines.schedule === null && deadlines.requirement !== 'ended_under';
}

/** The period a payroll tax is for, and anything that changes what the figure means. */
function depositDetail(obligation: DepositObligation): string {
  const parts = [`${obligation.label}.`];
  if (obligation.paidCents > 0) {
    parts.push(`${formatCents(obligation.paidCents)} of ${formatCents(obligation.owedCents)} is already paid.`);
  }
  if (obligation.status === 'overdue') parts.push(`It was due ${obligation.dueDate}.`);
  return parts.join(' ');
}

export function buildForecast(input: ForecastInput): CashflowResult {
  const items: CashflowItem[] = [];
  const undated: UndatedDeposit[] = [];

  /* ------------------------------------------ payroll taxes, from the plan */
  for (const obligation of input.deposits) {
    /*
     * WHAT IS STILL OUTSTANDING, NOT WHAT WAS ACCRUED. The plan has already
     * laid every bank payment against the liability it paid, so a deposit paid
     * early is `paid` and owes nothing here, and a part-paid one forecasts only
     * the rest. Voiding a run deletes its draft, or empties it when the draft is
     * the bank's own transaction, and a reversed run's entry is offset by its
     * reversal, so neither leaves anything in the plan to begin with.
     *
     * EACH DOLLAR ONCE. Every dollar accrued on 2410 to 2450 belongs to exactly
     * one obligation. The filings -- Form 941, the Texas wage report, Form 940
     * -- are not in the plan at all; where a return is also the payment (a
     * quarter under $2,500, Texas every quarter, FUTA of $500 or less), the
     * plan's obligation IS that payment, dated with the return.
     */
    if (obligation.status === 'paid') continue;
    if (obligation.dueDate === null) {
      undated.push({
        firstPayDate: obligation.from,
        lastPayDate: obligation.to,
        amountCents: obligation.outstandingCents,
        certainty: 'known',
      });
      continue;
    }
    /*
     * OVERDUE IS OWED NOW, so it goes at the start of the window rather than
     * on a due date the window has already passed. Dropped, the forecast would
     * look cheaper by exactly the payment that is most urgent. An overdue
     * INVOICE is the opposite case and stays out: it arrives whenever the
     * customer pays, which nobody knows, while a tax leaves the day it is paid,
     * which is now.
     */
    const overdue = obligation.status === 'overdue';
    items.push({
      date: overdue ? input.from : obligation.dueDate,
      label: overdue ? `${obligation.title} (Overdue)` : obligation.title,
      detail: depositDetail(obligation),
      amountCents: -obligation.outstandingCents,
      certainty: 'known',
      source: 'payroll',
      link: { kind: 'deposit', id: obligation.key },
    });
  }

  /* --------------------------------------------------------- bills and invoices */
  for (const bill of input.bills) {
    // No terms recorded means no date to place it on. Migration 0036: an
    // invented due date is worse than an omitted one, so it is omitted.
    if (bill.dueDate === null) continue;
    items.push({
      date: bill.dueDate,
      label: bill.counterparty ?? 'Bill',
      detail: bill.invoiceReference ?? 'No reference',
      amountCents: -bill.outstandingCents,
      certainty: 'due',
      source: 'bill',
      link: { kind: 'entry', id: bill.headerId },
    });
  }

  for (const invoice of input.invoices) {
    if (invoice.dueDate === null) continue;
    if (invoice.outstandingCents <= 0) continue;
    items.push({
      date: invoice.dueDate,
      label: invoice.customer ?? 'Invoice',
      detail: invoice.invoiceReference ?? 'No number',
      amountCents: invoice.outstandingCents,
      certainty: 'due',
      source: 'invoice',
      link: { kind: 'entry', id: invoice.headerId },
    });
  }

  /* ------------------------------------------------------ standing commitments */
  for (const commitment of input.commitments) {
    for (const date of occurrencesBetween(commitment, input.from, input.to)) {
      items.push({
        date,
        label: commitment.label,
        detail: `${commitment.cadence} to ${commitment.partyName ?? commitment.counterparty ?? 'a payee'}, recorded ${commitment.startsOn}.`,
        // Negative: a commitment is money leaving. Migration 0037 stores
        // outflows only -- an expected receipt is an invoice with terms.
        amountCents: -commitment.amountCents,
        certainty: 'committed',
        source: 'commitment',
        link: { kind: 'commitment', id: commitment.id },
      });
    }
  }

  /* --------------------------------------------------------- projected payroll */
  if (input.projected !== null) {
    const { payDate, preview } = input.projected;
    /*
     * The net pay leaves on payday and the tax leaves on its deposit date --
     * two movements, not one. A single line for the gross would be a figure
     * that never leaves the account on any day.
     */
    items.push({
      date: payDate,
      label: 'Payroll (Projected)',
      detail: `Net pay for ${preview.employee.name}, if the next run is made on this date.`,
      amountCents: -preview.result.netPayCents,
      certainty: 'projected',
      source: 'projected-payroll',
    });

    if (undatedDeposit(preview.deadlines)) {
      undated.push({ firstPayDate: payDate, lastPayDate: payDate, amountCents: preview.form941LiabilityCents, certainty: 'projected' });
    }

    for (const deadline of preview.deadlines.items) {
      if (deadline.kind !== 'federal_deposit') continue;
      items.push({
        date: deadline.date,
        // The server's name for the deadline, so a projected deposit and a real one read alike.
        label: `${deadline.label} (Projected)`,
        detail: `On a payroll of ${preview.employee.name} dated ${payDate}.`,
        amountCents: -preview.form941LiabilityCents,
        certainty: 'projected',
        source: 'projected-payroll',
      });
    }
  }

  /* ------------------------------------------------------------------ the window */
  const within = items
    .filter((item) => item.date >= input.from && item.date <= input.to)
    .sort((a, b) => a.date.localeCompare(b.date) || a.label.localeCompare(b.label));

  const outflowCents = { ...ZERO };
  const inflowCents = { ...ZERO };
  for (const item of within) {
    if (item.amountCents < 0) outflowCents[item.certainty] -= item.amountCents;
    else inflowCents[item.certainty] += item.amountCents;
  }

  return { items: within, undated, outflowCents, inflowCents, from: input.from, to: input.to };
}
